/**
 * The period and filters a dashboard or a saved report OPENS with — its saved
 * view — against the ones on screen.
 *
 * The bar is the viewer's, the owner's included: changing it is never an edit
 * (docs/reporting/requirements.md FR-4.8). So when an owner saves and the bar
 * differs from the saved view, the page ASKS which one to keep — see
 * `SaveFiltersDialog`. These answer whether to ask, and what each answer saves.
 */
import {
  type FilterNode,
  isFilterGroup,
  fieldLabel as labelInModel,
  type ReportModel,
} from '@clockwise/semantic-core';
import type { DateRangeValue } from '@clockwise/shared/date-range';
import { dateRangeLabel } from '@clockwise/ui';
import { encodeFilters, toSavedFilters } from '@/lib/filter-url';
import { type SavedPeriod, samePeriod, toSavedPeriod } from '@/lib/saved-period';

export interface SavedView {
  filters: FilterNode[];
  period: SavedPeriod;
}

/** What differs between the bar and the saved view — the popup's words. */
export type ViewChange = 'filters' | 'period' | 'period and filters';

/**
 * The view on screen as it would be saved — or `null` when it IS the saved
 * one, and there is nothing to ask.
 *
 * Filters are compared as links, for the reason `writeFilters` gives; the
 * period by `samePeriod`. A period that cannot be saved (All time) keeps the
 * saved one, so the filters beside it can still be.
 */
export function changedView(input: {
  filters: readonly FilterNode[];
  period: DateRangeValue;
  savedFilters: readonly FilterNode[];
  savedPeriod: SavedPeriod;
}): (SavedView & { changed: ViewChange }) | null {
  const filters = toSavedFilters(input.filters);
  const filtersChanged = encodeFilters(filters) !== encodeFilters(input.savedFilters);
  const period = samePeriod(input.savedPeriod, input.period) ? null : toSavedPeriod(input.period);
  if (!filtersChanged && !period) return null;
  const changed: ViewChange =
    filtersChanged && period ? 'period and filters' : period ? 'period' : 'filters';
  return { filters, period: period ?? input.savedPeriod, changed };
}

/**
 * A filter's field as its chip names it — a dimension's label, else a
 * measure's, else the id. `modelId` narrows the search to one model; without
 * it the first model that knows the field answers (a dashboard's tiles span
 * several).
 */
export function fieldLabel(
  models: readonly ReportModel[] | undefined,
  field: string,
  modelId?: string,
): string {
  for (const model of models ?? []) {
    if (modelId && model.id !== modelId) continue;
    // semantic-core's per-model answer, which falls back to the id itself.
    const label = labelInModel(model, field);
    if (label !== field) return label;
  }
  return field;
}

/** "Last month · Role and Customer", or "Last month · no filters". */
export function describeView(view: SavedView, labelOf: (field: string) => string): string {
  const when = dateRangeLabel({
    preset: view.period.preset ?? 'custom',
    from: view.period.from,
    to: view.period.to,
  });
  const rules = view.filters.flatMap((node) => (isFilterGroup(node) ? node.children : [node]));
  const names = [...new Set(rules.map((rule) => labelOf(rule.field)))];
  if (names.length === 0) return `${when} · no filters`;
  const listed =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${when} · ${listed}`;
}
